'use client';

import Link from 'next/link';
import {
  FOR_LIST,
  GENERAL_LINKS,
  MENU_ITEMS,
  SUPPORT_LEGAL_LINKS,
} from '@/src/constants/constants';
import { useBoolean } from '@/src/hooks/use-boolean';
import { Icon } from '../ui/Icon';
import { cn } from '@workspace/ui/lib/utils';
import { ButtonsBlockHome } from '@/src/sections/home/components/ButtonsBlock';
import { useProfile } from '@/src/hooks/use-profile';
import { HeaderActions } from '../ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu';
import { Button } from '@workspace/ui/components/button';
import Image from 'next/image';
import { HomeOptions } from '../ui/HomeOptions';

export const HeaderHome = () => {
  const isOpened = useBoolean(false);
  const isOpenCreators = useBoolean(false);
  const { data: profile } = useProfile();
  const isAnonymous = profile?.type === 'anonymous';
  return (
    <>
      <header
        className={cn(
          'w-full sticky top-5 bg-blue-home z-50 lg:p-2! lg:pl-4! p-2! header-wrapper max-h-[40px] lg:max-h-[56px] flex items-center justify-between rounded-[8px] gap-1',
          {
            'rounded-b-none': isOpened.value,
          },
        )}
      >
        <div className="flex md:gap-4 gap-1 items-center justify-between">
          <div className="flex md:gap-9 items-center">
            <div className="flex md:gap-4 items-center">
              <Link href="/">
                <Image
                  src="/icons/logoHero.png"
                  alt="logo"
                  width={40}
                  height={40}
                  priority
                  className="object-contain"
                />
              </Link>
              <Link href="/">
                <Image
                  src="/icons/logo.svg"
                  alt="logo"
                  width={115}
                  height={19}
                  priority
                  className="object-contain w-[115px] h-auto md:block hidden"
                />
              </Link>
              <div className="hidden xl:block">
                <HomeOptions>
                  <Button variant="ghost" size="icon">
                    <Icon name="dots" className="size-4 text-black" />
                  </Button>
                </HomeOptions>
              </div>
            </div>
          </div>
          <button
            onClick={() => isOpened.onToggle()}
            className="xl:hidden flex"
          >
            {isOpened.value ? (
              <Icon name="close-menu" className="size-6 text-black" />
            ) : (
              <Icon name="burger-menu" className="size-6 text-black" />
            )}
          </button>
        </div>

        <nav
          className={cn(
            'flex gap-2 md:gap-4 absolute top-10 md:top-10 lg:top-12 left-0 bg-blue-home text-sm md:text-base text-black flex-col w-full items-center justify-center shadow-[0_4px_4px_rgba(0,0,0,0.1)] pt-0 max-h-0 overflow-hidden transition-all duration-300 ease-in-out xl:flex-row xl:gap-8 xl:static xl:p-0 xl:shadow-none xl:max-h-none xl:w-fit xl:bg-none xl:bg-transparent font-grotesk font-medium rounded-b-none',
            {
              'rounded-b-[8px] max-h-[600px] p-2 pb-1 overflow-y-auto':
                isOpened.value,
              'top-7': isAnonymous,
            },
          )}
        >
          <span className="w-full h-[0.5px] xl:hidden bg-black/50" />

          {MENU_ITEMS.map(({ id, name, href }) => {
            const isCreators = name === 'For Gamers';

            return (
              <Link
                key={id}
                href={href}
                className="hover:text-white text-b flex items-center"
                onClick={isOpened.onToggle}
              >
                {isCreators ? (
                  <DropdownMenu
                    modal={false}
                    onOpenChange={(open) => isOpenCreators.setValue(open)}
                  >
                    <DropdownMenuTrigger asChild>
                      <div className="flex items-center hover:cursor-pointer">
                        {name}
                        <Icon
                          name="chevron-down"
                          width={12}
                          height={12}
                          className={cn(
                            'text-black lg:size-3 size-2.5 ml-3 transition-transform hover:text-white',
                            {
                              'rotate-180': isOpenCreators.value,
                            },
                          )}
                        />
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="center"
                      className="p-4 w-60 text-black/40 font-light leading-none mt-4"
                    >
                      {FOR_LIST.map(({ id, item }) => (
                        <DropdownMenuItem
                          key={id}
                          className={cn(
                            'p-0 text-black font-semibold font-grotesk',
                            {
                              'mt-3': id !== 1,
                            },
                          )}
                        >
                          {item}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  name
                )}
              </Link>
            );
          })}
          <span className="w-full h-[0.5px] xl:hidden bg-black/50" />
          <div className="xl:hidden w-full flex flex-col items-center">
            <div className="text-xs font-bold mb-2 text-[#42576CCC] uppercase">
              General
            </div>
            {GENERAL_LINKS.map(({ id, name, href, disabled, external }) => (
              <Link
                key={id}
                href={href}
                className={cn(
                  'hover:text-white text-sm mb-1 w-full text-center',
                  {
                    'opacity-50 pointer-events-none': disabled,
                  },
                )}
                onClick={isOpened.onToggle}
                {...(external && !href.startsWith('mailto:')
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {})}
              >
                {name}
              </Link>
            ))}
          </div>

          <span className="w-full h-[0.5px] xl:hidden bg-black/50" />

          <div className="xl:hidden w-full flex flex-col items-center">
            <div className="text-xs font-bold mb-2 text-[#42576CCC] uppercase">
              Support & Legal
            </div>
            {SUPPORT_LEGAL_LINKS.map(
              ({ id, name, href, disabled, external }) => (
                <Link
                  key={id}
                  href={href}
                  className={cn(
                    'hover:text-white text-sm mb-3 w-full text-center',
                    {
                      'opacity-50 pointer-events-none': disabled,
                    },
                  )}
                  onClick={isOpened.onToggle}
                  {...(external && !href.startsWith('mailto:')
                    ? { target: '_blank', rel: 'noopener noreferrer' }
                    : {})}
                >
                  {name}
                </Link>
              ),
            )}
          </div>
        </nav>

        {!isAnonymous ? (
          <HeaderActions isHome profile={profile} />
        ) : (
          <div className="flex items-center lg:gap-4 gap-1 lg:h-10">
            <ButtonsBlockHome isAnonymous={isAnonymous} />
          </div>
        )}
      </header>
    </>
  );
};
